import { forwardRef, useRef, useState } from 'react';
import { Check, Copy, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { Input, type InputProps } from '@/shared/ui/input';
import { cn } from '@/shared/lib/cn';

/** Campo sensível com toggle de visibilidade e copiar — padrão do app (login / senhas). */
export const SecretInput = forwardRef<HTMLInputElement, InputProps>(function SecretInput(
  { className, value, defaultValue, disabled, ...props },
  ref,
) {
  const [visible, setVisible] = useState(false);
  const [copied, setCopied] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const setRef = (node: HTMLInputElement | null) => {
    inputRef.current = node;
    if (typeof ref === 'function') ref(node);
    else if (ref) ref.current = node;
  };

  const copy = async () => {
    const text = String(value ?? inputRef.current?.value ?? defaultValue ?? '');
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('Copiado.');
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Não foi possível copiar.');
    }
  };

  const iconBtn =
    'grid size-7 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-accent/60 hover:text-foreground disabled:pointer-events-none disabled:opacity-40';

  return (
    <div className="relative">
      <Input
        ref={setRef}
        type={visible ? 'text' : 'password'}
        value={value}
        defaultValue={defaultValue}
        disabled={disabled}
        className={cn('pr-[4.25rem]', className)}
        {...props}
      />
      <div className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center gap-0.5">
        <button
          type="button"
          disabled={disabled}
          className={iconBtn}
          aria-label={visible ? 'Ocultar valor' : 'Mostrar valor'}
          onClick={() => setVisible((v) => !v)}
        >
          {visible ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
        </button>
        <button
          type="button"
          disabled={disabled}
          className={iconBtn}
          aria-label="Copiar"
          onClick={copy}
        >
          {copied ? <Check className="size-4 text-success" /> : <Copy className="size-4" />}
        </button>
      </div>
    </div>
  );
});
